/* eslint-disable */
import apiClient from './apiClient';

export interface InsuranceCompany {
    id: number;
    name: string;
    code: string;
    description?: string;
    phone?: string;
    email?: string;
    address?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface InsurancePlan {
    id: number;
    company_id?: number;
    company?: InsuranceCompany;
    name: string;
    code: string;
    coverage_percentage?: number;
    max_annual_amount?: number;
    description?: string;
    created_at?: string;
    updated_at?: string;
}

export interface PatientInsurance {
    id: number;
    patient_id: number;
    insurance_company_plan_id: number;
    company?: InsuranceCompany | null;
    plan?: InsurancePlan | null;
    insurance_company_plan?: {
        id: number;
        insurance_company_id?: number;
        plan_id?: number;
        company?: InsuranceCompany | null;
        plan?: InsurancePlan | null;
    };
    policy_number: string;
    nss?: string;
    card_number?: string | null;
    start_date: string | null;
    end_date: string | null;
    is_primary: boolean;
    created_at?: string;
    updated_at?: string;
}

function flattenPatientInsurance(row: PatientInsurance): PatientInsurance {
    const nested = row.insurance_company_plan;
    return {
        ...row,
        company: row.company ?? nested?.company ?? null,
        plan: row.plan ?? nested?.plan ?? null,
    };
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
    last_page?: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// ==================== Insurance Company Service ====================

export const insuranceCompanyService = {
    async getAll(params: { page?: number; per_page?: number; search?: string; is_active?: boolean } = {}): Promise<PaginatedResponse<InsuranceCompany>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<PaginatedResponse<InsuranceCompany>>(`/insurance-companies?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<InsuranceCompany> {
        const response = await apiClient.get<ApiResponse<InsuranceCompany>>(`/insurance-companies/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        description?: string;
        phone?: string;
        email?: string;
        address?: string;
        is_active?: boolean;
    }): Promise<InsuranceCompany> {
        const response = await apiClient.post<ApiResponse<InsuranceCompany>>('/insurance-companies', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        description: string;
        phone: string;
        email: string;
        address: string;
        is_active: boolean;
    }>): Promise<InsuranceCompany> {
        const response = await apiClient.put<ApiResponse<InsuranceCompany>>(`/insurance-companies/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/insurance-companies/${id}`);
    },
};

// ==================== Insurance Plan Service ====================

export const insurancePlanService = {
    async getAll(params: { page?: number; per_page?: number; search?: string; company_id?: number } = {}): Promise<PaginatedResponse<InsurancePlan>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.company_id) queryParams.append('company_id', params.company_id.toString());

        const response = await apiClient.get<PaginatedResponse<InsurancePlan>>(`/insurance-plans?${queryParams.toString()}`);
        return response.data;
    },

    async getByCompany(companyId: number): Promise<InsurancePlan[]> {
        const response = await apiClient.get<ApiResponse<InsurancePlan[]>>(`/insurance-plans/by-company/${companyId}`);
        return response.data.data || [];
    },

    async getById(id: number): Promise<InsurancePlan> {
        const response = await apiClient.get<ApiResponse<InsurancePlan>>(`/insurance-plans/${id}`);
        return response.data.data;
    },

    async create(data: {
        company_id: number;
        name: string;
        code: string;
        coverage_percentage?: number;
        max_annual_amount?: number;
        description?: string;
    }): Promise<InsurancePlan> {
        const response = await apiClient.post<ApiResponse<InsurancePlan>>('/insurance-plans', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        company_id: number;
        name: string;
        code: string;
        coverage_percentage: number;
        max_annual_amount: number;
        description: string;
    }>): Promise<InsurancePlan> {
        const response = await apiClient.put<ApiResponse<InsurancePlan>>(`/insurance-plans/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/insurance-plans/${id}`);
    },
};

// ==================== Patient Insurance Service ====================

export const patientInsuranceService = {
    async getByPatient(patientId: number): Promise<PatientInsurance[]> {
        const response = await apiClient.get<ApiResponse<PatientInsurance[]>>(`/patients/${patientId}/insurances`);
        return (response.data?.data ?? []).map(flattenPatientInsurance);
    },

    async addToPatient(patientId: number, data: {
        insurance_company_plan_id: number;
        policy_number?: string;
        nss?: string;
        card_number?: string;
        start_date?: string;
        end_date?: string;
        is_primary?: boolean;
    }): Promise<PatientInsurance> {
        const response = await apiClient.post<ApiResponse<PatientInsurance>>(`/patients/${patientId}/insurances`, data);
        return response.data.data;
    },

    async updatePatientInsurance(patientId: number, insuranceId: number, data: Partial<{
        insurance_company_plan_id: number;
        policy_number: string;
        nss: string;
        card_number: string;
        start_date: string;
        end_date: string;
        is_primary: boolean;
    }>): Promise<PatientInsurance> {
        const response = await apiClient.put<ApiResponse<PatientInsurance>>(`/patients/${patientId}/insurances/${insuranceId}`, data);
        return response.data.data;
    },

    async removeFromPatient(patientId: number, insuranceId: number): Promise<void> {
        await apiClient.delete(`/patients/${patientId}/insurances/${insuranceId}`);
    },
};

export default {
    insuranceCompanyService,
    insurancePlanService,
    patientInsuranceService,
};
